import { useEffect, useState } from 'react'
import { mutationLockStore, type MutationLockState } from '@/services/mutationLockStore'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import '@/styles/components/mutation-lock-overlay.css'

const MutationLockOverlay = (): React.JSX.Element | null => {
  const [state, setState] = useState<MutationLockState>(mutationLockStore.getState())

  useEffect(() => {
    return mutationLockStore.subscribe(setState)
  }, [])

  useEffect(() => {
    if (state.status !== 'pending') {
      return
    }
    return lockBodyScroll()
  }, [state.status])

  if (state.status === 'idle') {
    return null
  }

  const handleBackdropClick = () => {
    if (state.status === 'error' || state.status === 'success') {
      mutationLockStore.dismiss()
    }
  }

  return (
    <div
      className={`mutation-lock-overlay mutation-lock-overlay--${state.status}`}
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      onClick={handleBackdropClick}
    >
      <div className="mutation-lock-card" onClick={(event) => event.stopPropagation()}>
        {state.status === 'pending' && (
          <>
            <div className="mutation-lock-spinner" aria-hidden="true" />
            <p className="mutation-lock-message">
              {state.message ?? 'Saving your change…'}
            </p>
            <p className="mutation-lock-subtle">Please wait — this can take a moment.</p>
          </>
        )}

        {state.status === 'success' && (
          <>
            <div className="mutation-lock-tick" aria-hidden="true">
              <svg viewBox="0 0 52 52">
                <circle className="mutation-lock-tick__circle" cx="26" cy="26" r="24" fill="none" />
                <path
                  className="mutation-lock-tick__check"
                  fill="none"
                  d="M14 27l8 8 16-18"
                />
              </svg>
            </div>
            <p className="mutation-lock-message">{state.message ?? 'Saved'}</p>
          </>
        )}

        {state.status === 'error' && (
          <>
            <div className="mutation-lock-error-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86l-7.4 12.79A2 2 0 004.6 20h14.8a2 2 0 001.71-3.35l-7.4-12.79a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <p className="mutation-lock-message">{state.message ?? 'Try again soon.'}</p>
            <button
              type="button"
              className="mutation-lock-dismiss"
              onClick={() => mutationLockStore.dismiss()}
            >
              Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default MutationLockOverlay
